import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl-next';
import SectionMessage from '@atlaskit/section-message';
import { CC_ERROR_MESSAGE } from '../../common/constants/breadcrumb-names';
import { getUserFriendlyFailureMessage } from '../../common/utils/errors';
import { BindingLevels } from '../../common/utils/errors/error-ui-associations';
import { Breadcrumb } from '../../common/utils/events';
import { CommerceIntlProvider } from '../../common/utils/locale';
import { isError, isException } from '../../common/utils/resultful';
import { useCreditCardFailure } from '../../controllers/hooks/use-credit-card-failure';
import { messages } from './messages';
var BaseCreditCardErrorMessage = function BaseCreditCardErrorMessage(_ref) {
  var className = _ref.className,
    failure = _ref.failure,
    _ref$autoScroll = _ref.autoScroll,
    autoScroll = _ref$autoScroll === void 0 ? true : _ref$autoScroll;
  var errorRef = useRef(null);
  useEffect(function () {
    if (errorRef.current !== null && failure !== undefined && autoScroll) {
      errorRef.current.scrollIntoView();
    }
  }, [errorRef, failure, autoScroll]);
  if (failure === undefined) {
    return null;
  }
  var failureSpecificUserFriendlyMessage = getUserFriendlyFailureMessage(failure);
  var userFriendlyMessage = failureSpecificUserFriendlyMessage !== undefined ? failureSpecificUserFriendlyMessage : /*#__PURE__*/React.createElement(FormattedMessage, messages.error);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    React.createElement("div", {
      role: "alert",
      className: className,
      ref: errorRef
    }, /*#__PURE__*/React.createElement(SectionMessage, {
      testId: 'credit-card.error-message',
      appearance: "error"
    }, userFriendlyMessage))
  );
};
var createErrorMessageHandlerComponent = function createErrorMessageHandlerComponent(showFailureCallback) {
  var Component = function Component(_ref2) {
    var className = _ref2.className,
      autoScroll = _ref2.autoScroll;
    var failure = useCreditCardFailure();
    if (failure === undefined) {
      return null;
    }
    var showFailure = showFailureCallback(failure);
    return showFailure ? /*#__PURE__*/React.createElement(CommerceIntlProvider, null, /*#__PURE__*/React.createElement(Breadcrumb, {
      name: CC_ERROR_MESSAGE
    }, /*#__PURE__*/React.createElement(BaseCreditCardErrorMessage
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    , {
      className: className,
      autoScroll: autoScroll,
      failure: failure
    }))) : null;
  };
  return Component;
};

/**
 * This component shows error message shows generic page-level errors - Errors we can't really
 * associate with a particular action or component.
 */
export var CreditCardPageErrorMessage = createErrorMessageHandlerComponent(function (failure) {
  return isException(failure) || isError(failure) && failure.error.association.type === BindingLevels.NONE;
});

/**
 * This component shows error messages related to errors in the CC form
 */
export var CreditCardFormErrorMessage = createErrorMessageHandlerComponent(function (failure) {
  return isError(failure) && failure.error.association.type === BindingLevels.INPUT_GROUP;
});

/**
 * This component shows error messages related to CC detail submissions
 */
export var CreditCardSubmissionErrorMessage = createErrorMessageHandlerComponent(function (failure) {
  return isError(failure) && failure.error.association.type === BindingLevels.ACTION;
});

/**
 * Use this if you want the CC package to decide when to show errors for you but don't care about the positioning.
 * It combines {@link CreditCardSubmissionErrorMessage}, {@link CreditCardFormErrorMessage} and {@link CreditCardPageErrorMessage}
 */
export var CreditCardErrorMessage = function CreditCardErrorMessage(_ref3) {
  var className = _ref3.className,
    autoScroll = _ref3.autoScroll;
  // Note that only 1 of these components should be shown at a time
  return /*#__PURE__*/React.createElement(CommerceIntlProvider, null, /*#__PURE__*/React.createElement(CreditCardPageErrorMessage
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
  , {
    className: className,
    autoScroll: autoScroll
  }), /*#__PURE__*/React.createElement(CreditCardFormErrorMessage
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
  , {
    className: className,
    autoScroll: autoScroll
  }), /*#__PURE__*/React.createElement(CreditCardSubmissionErrorMessage
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
  , {
    className: className,
    autoScroll: autoScroll
  }));
};